import { useState } from 'react'
import { useFinancialPlanStore, Income } from '../../store/financialPlanStore'
import { Plus, Trash2, TrendingUp } from 'lucide-react'

export function IncomeStep() {
  const { currentPlan, addIncome, removeIncome } = useFinancialPlanStore()
  const [showForm, setShowForm] = useState(false)
  
  const [newIncome, setNewIncome] = useState<Omit<Income, 'id'>>({
    source: 'Salary',
    amount: 0,
    startAge: 30,
    growthRate: 3,
    frequency: 'Annual',
  })
  
  if (!currentPlan) return null
  
  const client = currentPlan.familyMembers.find(m => m.relationship === 'client')
  const currentAge = client ? new Date().getFullYear() - new Date(client.dateOfBirth).getFullYear() : 30
  
  const handleAdd = () => {
    if (newIncome.amount > 0) {
      addIncome(newIncome)
      setNewIncome({
        source: 'Salary',
        amount: 0,
        startAge: currentAge,
        growthRate: 3,
        frequency: 'Annual',
      })
      setShowForm(false)
    }
  }
  
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value)
  
  const totalIncome = currentPlan.income.reduce((sum, inc) => sum + inc.amount, 0)
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
        <p className="text-sm font-medium text-green-900 mb-1">Total Annual Income</p>
        <p className="text-3xl font-bold text-green-900">{formatCurrency(totalIncome)}</p>
      </div>
      
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-primary-600" />
          Income Sources
        </h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center">
            <Plus className="h-4 w-4 mr-1" />
            Add Income
          </button>
        )}
      </div>
      
      <div className="space-y-3">
        {currentPlan.income.map(income => (
          <div key={income.id} className="card border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <h4 className="font-medium">{income.source}</h4>
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                    {income.frequency}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Amount</p>
                    <p className="font-medium">{formatCurrency(income.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Start Age</p>
                    <p className="font-medium">{income.startAge}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Growth Rate</p>
                    <p className="font-medium">{income.growthRate}%</p>
                  </div>
                  {income.endAge && (
                    <div>
                      <p className="text-xs text-gray-500">End Age</p>
                      <p className="font-medium">{income.endAge}</p>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => removeIncome(income.id)} className="text-red-600 hover:text-red-700 ml-4">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showForm && (
        <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50">
          <h4 className="font-medium mb-4">Add Income Source</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Income Source *</label>
                <select
                  value={newIncome.source}
                  onChange={(e) => setNewIncome({ ...newIncome, source: e.target.value as Income['source'] })}
                  className="input-field"
                >
                  <option value="Salary">Salary/Wages</option>
                  <option value="Bonus">Bonus</option>
                  <option value="Social Security">Social Security</option>
                  <option value="Pension">Pension</option>
                  <option value="Rental">Rental Income</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Annual Amount *</label>
                <input
                  type="number"
                  value={newIncome.amount}
                  onChange={(e) => setNewIncome({ ...newIncome, amount: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  min="0"
                  step="1000"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Start Age *</label>
                <input
                  type="number"
                  value={newIncome.startAge}
                  onChange={(e) => setNewIncome({ ...newIncome, startAge: parseInt(e.target.value) || 0 })}
                  className="input-field"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="label">End Age (Optional)</label>
                <input
                  type="number"
                  value={newIncome.endAge || ''}
                  onChange={(e) => setNewIncome({ ...newIncome, endAge: parseInt(e.target.value) || undefined })}
                  className="input-field"
                  min="0"
                  max="100"
                  placeholder="Leave blank if ongoing"
                />
              </div>
              <div>
                <label className="label">Growth Rate (%)</label>
                <input
                  type="number"
                  value={newIncome.growthRate}
                  onChange={(e) => setNewIncome({ ...newIncome, growthRate: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  step="0.5"
                />
              </div>
            </div>
            
            {newIncome.source === 'Pension' && (
              <div>
                <label className="label">COLA % (Annual Increase)</label>
                <input
                  type="number"
                  value={newIncome.cola || ''}
                  onChange={(e) => setNewIncome({ ...newIncome, cola: parseFloat(e.target.value) || undefined })}
                  className="input-field"
                  step="0.5"
                />
              </div>
            )}
            
            <div className="flex space-x-3">
              <button onClick={handleAdd} className="btn-primary">Add Income</button>
              <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {currentPlan.income.length === 0 && !showForm && (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>No income sources added yet</p>
          <p className="text-sm mt-1">Add your salary, Social Security, pension, etc.</p>
        </div>
      )}
    </div>
  )
}
